import React, { useEffect, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { ICarDetail } from 'src/redux/carDetails/carDetails.duck.interface';
import Spinner from '@components/Spinner';
import { getMonthlyPriceObjByType, getTotalPriceObjByType } from '@utils/Price.utils';
import { CarDetailsService, CarJourneyType, IFeature, IPriceV2Types } from 'src/services';
import PriceSuffix from '@components/PriceSuffix';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitch';
import { orderBy } from 'lodash';
import { buildParameterizedPathname, extractParamsFromPathname } from '@utils/url.utils';
import { useRouter } from 'next/router';
import routes from 'src/constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { IGlobalState } from 'src/redux';
import GlobalDuck from 'src/redux/global/global.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { EMPLOYEE_PRICE_TYPE } from 'src/services/filters/filters';
import { EContextPage, findPromoByPageAndType } from '@utils/Promo.utils';
import MarketingSticker from '@components/MarketingSticker';
import { IPromo } from 'src/interfaces/IOfferItem';
import parser from 'bbcode-to-react';

const KEY_ALL = 'all';
const KEY_KEYS = 'key';
const KEY_PROPERTIES = 'property';
const KEY_TRIMS = 'trim';
const KEY_ENGINES = 'engine';
const KEY_COLORS = 'color';
const KEY_INTERIORS = 'interior';
const KEY_FEATURES = 'feature';
const KEY_FEATURES_DESCRIPTION = 'featureDescription';

const EXIST_FEATURE_IN_CONFIGURATION = 'existInConfiguration';
const NO_EXIST_FEATURE_IN_CONFIGURATION = 'noExistInConfiguration';
const SELECTED = 'selected';
const SCROLL_ELEMENT_CLASS_NAME = 'trim-compare';

export interface ITrimCompareStyled {
    className: string;
    countOfTrims?: number;
}

const ModalTrimCompareTemplate = ({ className }: ITrimCompareStyled) => {
    const { t } = useTranslations();
    const { formatMoney } = useMoneyFormatter();
    const router = useRouter();
    const dispatch = useDispatch();

    const [activeSection, setActiveSection] = useState(KEY_ALL);
    const [activeFeature, setActiveFeature] = useState(KEY_ALL);
    const [showSticky, setShowSticky] = useState(false);

    const [trims, setTrims] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const carJourney = useSelector<IGlobalState, CarJourneyType>((state) => GlobalDuck.getCarJourney(state));

    useEffect(() => {
        setIsLoading(true);

        CarDetailsService.getCarsDetailsWithAllOptions(router?.query?.nameplateBodyStyleSlug as string).then(
            (trims) => {
                setTrims(trims);
                setIsLoading(false);
            }
        );
    }, []);

    useEffect(() => {
        const element = document?.getElementsByClassName(SCROLL_ELEMENT_CLASS_NAME)?.[0];
        const trimsTitle = document?.getElementById(`TITLE_${KEY_TRIMS}`);
        const enginesTitle = document?.getElementById(`TITLE_${KEY_ENGINES}`);
        const colorsTitle = document?.getElementById(`TITLE_${KEY_COLORS}`);
        const interiorsTitle = document?.getElementById(`TITLE_${KEY_INTERIORS}`);
        const featuresTitle = document?.getElementById(`TITLE_${KEY_FEATURES}`);
        const featuresDescription = document?.getElementById(KEY_FEATURES_DESCRIPTION);

        if (element.clientWidth <= (trims?.length || 1) * 300) {
            element?.addEventListener('scroll', () => {
                setShowSticky(element?.scrollTop >= 200);
                trimsTitle.style.marginLeft = `${element?.scrollLeft}px`;
                enginesTitle.style.marginLeft = `${element?.scrollLeft}px`;
                colorsTitle.style.marginLeft = `${element?.scrollLeft}px`;
                interiorsTitle.style.marginLeft = `${element?.scrollLeft}px`;
                featuresTitle.style.marginLeft = `${element?.scrollLeft}px`;
                featuresDescription.style.marginLeft = `${element?.scrollLeft}px`;
            });
        }
    }, []);

    useEffect(() => {
        const trimsSection = document?.getElementById(`SECTION_${KEY_TRIMS}`);
        const propertiesSection = document?.getElementById(`SECTION_${KEY_PROPERTIES}`);
        const featureSection = document?.getElementById(`SECTION_${KEY_FEATURES}`);

        if (trimsSection) trimsSection.style.width = `${(trims?.length || 1) * 300}px`;
        if (propertiesSection) propertiesSection.style.width = `${(trims?.length || 1) * 300}px`;
        if (featureSection) featureSection.style.width = `${(trims?.length || 1) * 300}px`;
    }, [trims]);

    const handleColorClick = async (carDetails: ICarDetail, selectedExteriorColorSlug: string) => {
        setIsLoading(true);

        const newTrim = await CarDetailsService.getCarDetailsWithAllOptions(
            carDetails.nameplateBodyStyleSlug,
            carDetails.specPackSlug,
            carDetails.engineGearboxFuelSlug,
            selectedExteriorColorSlug,
            carDetails.interiorColourSlug,
            []
        );

        setTrims(
            trims.map((trim: { vehicle: ICarDetail; features: { engines: IFeature[] } }) => {
                return trim.vehicle.nameplateBodyStyleSlug === carDetails.nameplateBodyStyleSlug &&
                    trim.vehicle.specPackSlug === carDetails.specPackSlug &&
                    trim.vehicle.engineGearboxFuelSlug === carDetails.engineGearboxFuelSlug &&
                    trim.vehicle.interiorColourSlug === carDetails.interiorColourSlug &&
                    trim.vehicle.exteriorColourSlug === carDetails.exteriorColourSlug
                    ? newTrim
                    : trim;
            })
        );

        setIsLoading(false);

        const elementColor = document.getElementById(`SECTION_${KEY_PROPERTIES}`);
        const elementScroll = document.getElementsByClassName(SCROLL_ELEMENT_CLASS_NAME)?.[0];

        elementColor?.scrollIntoView();
        elementScroll.scrollLeft = 300 * Math.floor(elementScroll.scrollLeft / 300);

        return;
    };

    const getMainImageUrl = (vehicle: ICarDetail) => {
        const images = vehicle?.images?.filter((img: any) => img.type === 'Ext') ?? [{}];
        const mainImage = images?.splice(1, 1) ?? [{}];

        return mainImage[0]?.url;
    };

    const redirectToConfigurator = (vehicle: ICarDetail) => {
        dispatch(UIDuck.closeModal());

        const currentPathParams = extractParamsFromPathname(router.asPath);
        const newPathParams = {
            nameplateBodyStyleSlug: currentPathParams.nameplateBodyStyleSlug,
            speckPackSlug: vehicle?.specPackSlug,
            engineGearboxFuelSlug: vehicle?.engineGearboxFuelSlug,
            exteriorColourSlug: vehicle?.exteriorColourSlug,
            interiorColourSlug: vehicle?.interiorColourSlug,
        };

        const modelURL = buildParameterizedPathname(routes.CAR, carJourney, newPathParams);

        return router.push(modelURL);
    };

    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);
    const isPromoEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_PROMO_ENABLED);

    const hasAnyTrimOffers = trims?.filter((trim: { vehicle: ICarDetail }) =>
        findPromoByPageAndType(trim?.vehicle?.offers, EContextPage.trim)
    );

    const uniqueFeaturesKeys = [] as string[];
    const uniqueEngines = [] as IFeature[];
    const uniqueInteriorColors = [] as IFeature[];

    return (
        <div className={className} id="trims">
            {showSticky && (
                <div className="sticky">
                    {trims?.map((trim: { vehicle: ICarDetail }, trimKey: number) => (
                        <div key={`sticky-trim-${trimKey}`} className="trim">
                            <span className="trimName">{trim?.vehicle?.specPack?.title}</span>
                        </div>
                    ))}
                </div>
            )}
            <div className="trims" id={`SECTION_${KEY_TRIMS}`}>
                <span className="title" id={`TITLE_${KEY_TRIMS}`}>
                    {t('trim.comparator.title')}
                </span>
                {isLoading ? (
                    <Spinner border={2} size={25} color="black" />
                ) : (
                    <div className="sections">
                        {trims?.map((trim: { vehicle: ICarDetail }, trimKey: number) => {
                            const monthlyPrice = getMonthlyPriceObjByType(trim?.vehicle?.prices, EMPLOYEE_PRICE_TYPE);
                            const hasTrimOffers = findPromoByPageAndType(trim?.vehicle?.offers, EContextPage.trim);
                            const priceObj = getTotalPriceObjByType(
                                trim?.vehicle?.extraFields?.pricesV2 || trim?.vehicle?.pricesV2,
                                IPriceV2Types.TOTAL_B2C_CASH
                            );

                            return (
                                <div key={`trim-${trimKey}`} className="trim">
                                    {isPromoEnabled && hasTrimOffers && (
                                        <div className="trimPromo">
                                            <MarketingSticker
                                                data={
                                                    findPromoByPageAndType(
                                                        trim?.vehicle?.offers,
                                                        EContextPage.trim
                                                    ) as unknown as Array<IPromo>
                                                }
                                                page={EContextPage.trim}
                                            />
                                        </div>
                                    )}
                                    {isPromoEnabled && !hasTrimOffers && hasAnyTrimOffers && (
                                        <div className="trimPromoSpace" />
                                    )}
                                    <img className="trimImage" src={getMainImageUrl(trim?.vehicle)} />
                                    <span className="trimName">{trim?.vehicle?.specPack?.title}</span>
                                    <span className="trimLabel">
                                        {trim?.vehicle?.trimContent?.title
                                            ? parser.toReact(trim.vehicle.trimContent.title)
                                            : null}
                                    </span>
                                    <span className="trimDescription">
                                        {trim?.vehicle?.trimContent?.description
                                            ? parser.toReact(trim.vehicle.trimContent.description)
                                            : null}
                                    </span>
                                    <div className="alignToBottom">
                                        <span className="trimPrice">
                                            {t('nameplate.priceFrom')}{' '}
                                            {formatMoney(
                                                isSOLEnabled
                                                    ? priceObj?.breakdown.finalCustomerCashPriceOnTheRoad
                                                    : priceObj?.finalPriceInclTax,
                                                true
                                            )}{' '}
                                            {monthlyPrice && (
                                                <span className="monthlyPrice">
                                                    <span className="or">{t('configurator.optionPrice.suffix')}</span>{' '}
                                                    {formatMoney(monthlyPrice.amount, true)}
                                                    <PriceSuffix isMonthlyPrice hideAsterisk />
                                                </span>
                                            )}
                                        </span>
                                        <button onClick={() => redirectToConfigurator(trim?.vehicle)}>
                                            {t('trim.comparator.select')}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div className="keys" id={`SECTION_${KEY_KEYS}`}>
                <span className="label">{t('modal.features.keys.title')}</span>
                <div className="categories">
                    {[KEY_ALL, KEY_ENGINES, KEY_COLORS, KEY_INTERIORS, KEY_FEATURES].map((key, index) => (
                        <span
                            key={index}
                            className={activeSection === key ? SELECTED : ''}
                            onClick={() => setActiveSection(key)}
                        >
                            {t(`modal.features.keys.${key}`)}
                        </span>
                    ))}
                </div>
            </div>
            <div className="properties" id={`SECTION_${KEY_PROPERTIES}`}>
                {(activeSection === KEY_ALL || activeSection === KEY_ENGINES) && (
                    <div className="enginesAvailable" id={`SECTION_${KEY_ENGINES}`}>
                        <span className="label" id={`TITLE_${KEY_ENGINES}`}>
                            {t('modal.features.keys.engine')}
                        </span>
                        {isLoading ? (
                            <Spinner border={2} size={25} color="black" />
                        ) : (
                            <div className="sections">
                                {trims?.map((trim: { features: { engines: IFeature[] } }, trimKey: number) => {
                                    const allEngines = trim?.features?.engines;
                                    const differentEnginesWithPrevious = allEngines?.filter(
                                        ({ engineGearboxFuelSlug }) =>
                                            !uniqueEngines
                                                .map((uniqueEngine) => uniqueEngine.engineGearboxFuelSlug)
                                                .includes(engineGearboxFuelSlug)
                                    );

                                    return (
                                        <div key={`engines-${trimKey}`} className="engines">
                                            {[...uniqueEngines, ...differentEnginesWithPrevious].map(
                                                (motorization: IFeature, engineKey: number) => {
                                                    const getPower = () =>
                                                        motorization?.characteristics?.find(
                                                            (char: { key: string }) => char.key === 'maximumPowerKW'
                                                        )?.value;

                                                    if (
                                                        !uniqueEngines
                                                            .map((uniqueEngine) => uniqueEngine.engineGearboxFuelSlug)
                                                            .includes(motorization.engineGearboxFuelSlug)
                                                    ) {
                                                        uniqueEngines.push(motorization);
                                                    }

                                                    return (
                                                        <div
                                                            key={`${trimKey}-engine-${engineKey}`}
                                                            className={`engine ${
                                                                allEngines.find(
                                                                    ({ engineGearboxFuelSlug }) =>
                                                                        engineGearboxFuelSlug ===
                                                                        motorization.engineGearboxFuelSlug
                                                                )
                                                                    ? EXIST_FEATURE_IN_CONFIGURATION
                                                                    : NO_EXIST_FEATURE_IN_CONFIGURATION
                                                            }`}
                                                        >
                                                            <span className="details">
                                                                {`${motorization.fuel}, ${motorization.gearboxTitle}${
                                                                    motorization?.characteristics?.length >= 1
                                                                        ? `, ${getPower()} ${t(
                                                                              'configurator.info.power.label'
                                                                          )}`
                                                                        : ''
                                                                }`}
                                                            </span>
                                                            <span className="name">{motorization.title}</span>
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
                {(activeSection === KEY_ALL || activeSection === KEY_COLORS) && (
                    <div className="colors" id={`SECTION_${KEY_COLORS}`}>
                        <span className="label" id={`TITLE_${KEY_COLORS}`}>
                            {t('modal.features.color.title')}
                        </span>
                        {isLoading ? (
                            <Spinner border={2} size={25} color="black" />
                        ) : (
                            <div className="sections">
                                {trims?.map(
                                    (
                                        trim: { vehicle: ICarDetail; features: { exteriorColors: IFeature[] } },
                                        trimKey: number
                                    ) => (
                                        <div key={`exteriorColors-${trimKey}`} className="configurations">
                                            <img className="image" src={getMainImageUrl(trim?.vehicle)} />
                                            <div className="colorButtons">
                                                {trim?.features.exteriorColors?.map(
                                                    (exteriorColor, exteriorColorKey: number) => (
                                                        <button
                                                            key={`${trimKey}-exteriorColor-${exteriorColorKey}`}
                                                            className={`colorButton enabled ${
                                                                trim?.vehicle?.exteriorColourSlug ===
                                                                exteriorColor.exteriorColourSlug
                                                                    ? SELECTED
                                                                    : ''
                                                            }`}
                                                            role="button"
                                                            style={{
                                                                backgroundImage: `url('${exteriorColor.images[0]?.url}')`,
                                                            }}
                                                            onClick={() =>
                                                                handleColorClick(
                                                                    trim?.vehicle,
                                                                    exteriorColor.exteriorColourSlug
                                                                )
                                                            }
                                                        />
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                )}
                {(activeSection === KEY_ALL || activeSection === KEY_INTERIORS) && (
                    <div className="interiorsAvailable" id={`SECTION_${KEY_INTERIORS}`}>
                        <span className="label" id={`TITLE_${KEY_INTERIORS}`}>
                            {t('modal.features.keys.interior')}
                        </span>
                        {isLoading ? (
                            <Spinner border={2} size={25} color="black" />
                        ) : (
                            <div className="sections">
                                {trims?.map((trim: { features: { interiorColors: IFeature[] } }, trimKey: number) => {
                                    const allInteriorColors = trim?.features?.interiorColors;
                                    const differentInteriorColorsWithPrevious = allInteriorColors.filter(
                                        ({ id }) =>
                                            !uniqueInteriorColors
                                                .map((uniqueInteriorColor) => uniqueInteriorColor.id)
                                                .includes(id)
                                    );

                                    return (
                                        <div key={`interiorColors-${trimKey}`} className="interiors">
                                            {[...uniqueInteriorColors, ...differentInteriorColorsWithPrevious].map(
                                                (interiorColor: IFeature, interiorColorKey: number) => {
                                                    if (
                                                        !uniqueInteriorColors
                                                            .map((uniqueInteriorColor) => uniqueInteriorColor.id)
                                                            .includes(interiorColor.id)
                                                    ) {
                                                        uniqueInteriorColors.push(interiorColor);
                                                    }

                                                    return (
                                                        <div
                                                            key={`${trimKey}-interiorColor-${interiorColorKey}`}
                                                            className={`interior ${
                                                                allInteriorColors.find(
                                                                    ({ id }) => id === interiorColor.id
                                                                )
                                                                    ? EXIST_FEATURE_IN_CONFIGURATION
                                                                    : NO_EXIST_FEATURE_IN_CONFIGURATION
                                                            }`}
                                                        >
                                                            <span className="name">{interiorColor.title}</span>
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="keys withoutBorder">
                <span className="label" id={`TITLE_${KEY_FEATURES}`}>
                    {t('modal.features.feature.title')}
                </span>
                <p className="description" id={KEY_FEATURES_DESCRIPTION}>
                    {t('modal.features.feature.description')}
                </p>
                {isLoading ? (
                    <Spinner border={2} size={25} color="black" />
                ) : (
                    <div className="categories">
                        <span
                            className={activeFeature === KEY_ALL ? SELECTED : ''}
                            onClick={() => setActiveFeature(KEY_ALL)}
                        >
                            {t('modal.features.keys.all')}
                        </span>
                        {trims?.map((trim: { vehicle: ICarDetail }, trimKey: number) =>
                            orderBy(trim?.vehicle?.standardFeatures, ['category'], ['asc']).map(
                                (feature: { key: string; category: string }, featureKey: number) => {
                                    if (!uniqueFeaturesKeys.includes(feature.category)) {
                                        uniqueFeaturesKeys.push(feature.category);

                                        return (
                                            <span
                                                key={`features-${trimKey}-${featureKey}`}
                                                className={activeFeature === feature.category ? SELECTED : ''}
                                                onClick={() => setActiveFeature(feature.category)}
                                            >
                                                {feature.category}
                                            </span>
                                        );
                                    }
                                }
                            )
                        )}
                    </div>
                )}
            </div>
            <div id={`SECTION_${KEY_FEATURES}`}>
                {uniqueFeaturesKeys.map((uniqueFeatureCategory: string, renderedFeatureKey: number) => {
                    if (activeFeature === KEY_ALL || activeFeature === uniqueFeatureCategory) {
                        return (
                            <div key={`feature-${renderedFeatureKey}`} className="featuresAvailable">
                                <span className="label">{uniqueFeatureCategory}</span>
                                <div className="sections">
                                    {trims?.map((trim: { vehicle: ICarDetail }, trimKey: number) => (
                                        <div key={`${trimKey}-feature`} className="features">
                                            {trim?.vehicle?.standardFeatures
                                                ?.find(
                                                    (feature: { category: string }) =>
                                                        feature.category === uniqueFeatureCategory
                                                )
                                                ?.data?.map((configuration: { label: string }, key: number) => (
                                                    <div
                                                        key={`${trimKey}-feature-${configuration.label}-${key}`}
                                                        className="feature"
                                                    >
                                                        <span className="name">{configuration.label}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }
                })}
            </div>
        </div>
    );
};

export default ModalTrimCompareTemplate;
